import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import {
  catchError,
  map,
  Observable,
  shareReplay,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { SeminarDateSeriesListModel } from '../../../models/seminar.model';
import { SponsorCardModel } from '../../../models/sponsor-card.model';
import { TitleCardModel } from '../../../models/title-card.model';
import { TrainingCategoryModel } from '../../../models/training-category.model';
import { AlertService } from '../../../services/alert.service';
import { SeminarService } from '../../../services/seminar.service';
import { SponsorCardService } from '../../../services/sponsor-card.service';
import { TrainingCategoryService } from '../../../services/training-category.service';
import {
  getSeminarFobiPoints,
  getSeminarLecturerText,
} from '../../../utils/seminar.utils';

@Component({
  selector: 'app-advancedtraining',
  templateUrl: './advancedtraining.component.html',
  styleUrl: './advancedtraining.component.scss',
  standalone: false,
})
export class AdvancedtrainingComponent implements OnInit, OnDestroy {
  public dataSource$: Observable<any[]>;
  public seminarsLoading = true;
  public categoryLoading = true;
  public sponsorCards: SponsorCardModel[];
  public currentView: 'grid' | 'list' = 'list';
  public sponsorCardsLoading = true;

  public titleCard: TitleCardModel;

  public seminarCategory: TrainingCategoryModel;

  private destroy$ = new Subject<void>();

  constructor(
    private seminarService: SeminarService,
    private trainingCategoryService: TrainingCategoryService,
    public location: Location,
    private activatedRoute: ActivatedRoute,
    private sponsorCardService: SponsorCardService,
    private alertService: AlertService
  ) {}

  async ngOnInit() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(async params => {
        const id = params.get('id');
        if (id) {
          this.seminarsLoading = true;
          this.categoryLoading = true;
          this.sponsorCardsLoading = true;

          await this.getCourseType(+atob(id));

          this.titleCard = {
            title: this.seminarCategory.name,
            descriptionBold: this.seminarCategory.descriptionBold ?? '',
            description: this.seminarCategory.description ?? '',
            image: this.seminarCategory.titlePicture || null,
            color0: this.seminarCategory.color0,
            color50: this.seminarCategory.color50,
            color70: this.seminarCategory.color70,
            color80: this.seminarCategory.color80,
          };
          this.getSeminars();
          this.getSponsorCards(+atob(id));
        }
      });
  }

  private getSponsorCards(categoryId: number): void {
    this.sponsorCardService.getSponsorCardsByCategoryId(categoryId).subscribe({
      next: sponsorCards => {
        this.sponsorCards = sponsorCards;
        this.sponsorCardsLoading = false;
      },
      error: () => {
        this.sponsorCardsLoading = false;
      },
    });
  }

  private async getCourseType(id: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.trainingCategoryService.getTrainingCategoryById(id).subscribe({
        next: category => {
          this.seminarCategory = category;
          this.categoryLoading = false;
          resolve();
        },
        error: () => {
          this.alertService.showErrorAlert(
            'Die Kategorie konnte nicht geladen werden.'
          );
          this.categoryLoading = false;
          reject();
        },
      });
    });
  }

  public changeView(view: 'grid' | 'list') {
    this.currentView = view;
  }

  private getSeminars(): void {
    this.dataSource$ = this.seminarService
      .getSeminarsByCategory(this.seminarCategory.id)
      .pipe(
        take(1),
        map(seminarDateSeriesList => {
          this.seminarsLoading = false;
          return seminarDateSeriesList.map(seminarDateSeries => {
            return {
              ...seminarDateSeries,
              title: seminarDateSeries.seminar.title,
              dates: seminarDateSeries.dates,
              lecturer: getSeminarLecturerText(seminarDateSeries),
              fobiPoints: getSeminarFobiPoints(seminarDateSeries),
              status: this.getSeminarStatus(seminarDateSeries),
            };
          });
        }),
        catchError(error => {
          this.seminarsLoading = false;
          console.error('Error fetching seminars', error);
          this.alertService.showErrorAlert(
            'Die Seminare konnten nicht geladen werden.'
          );
          return [];
        }),
        shareReplay(1) // Cache the result
      );
  }

  private getSeminarStatus(
    seminarDateSeries: SeminarDateSeriesListModel
  ): string {
    if (
      seminarDateSeries.dates.find(item =>
        moment(item.startDate).isBefore(moment())
      ) &&
      seminarDateSeries.dates.find(item =>
        moment(item.endDate).isAfter(moment())
      )
    ) {
      return 'Läuft';
    }
    if (seminarDateSeries.seminar.isCanceled) {
      return 'Abgesagt';
    }
    return `${Math.max(0, seminarDateSeries.seminar.maxParticipants - seminarDateSeries.participantCount)} frei`;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
